import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Server,
  ShieldCheck,
  Wifi,
  WifiOff,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Mail,
  Send,
  MessageCircle,
  Plus,
  Trash2,
  ExternalLink,
  BadgeCheck,
  Clock,
  History,
  RotateCcw,
  Power,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const SirsSettingsPanel = () => {
  const { authAxios, user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testing, setTesting] = useState(false);
  // Telegram
  const [tg, setTg] = useState(null);
  const [tgUsername, setTgUsername] = useState("");
  const [tgBusy, setTgBusy] = useState(false);
  const [tgPolling, setTgPolling] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgAlertSaving, setTgAlertSaving] = useState(false);
  const [tgBotSaving, setTgBotSaving] = useState(false);
  // History
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const isSuperadmin = user && user.role === "superadmin";

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/sirs/mode")
      .then((r) => setState(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat status SIRS"))
      .finally(() => setLoading(false));
  };

  const loadTg = () => {
    authAxios
      .get("/admin/telegram/config")
      .then((r) => setTg(r.data))
      .catch(() => {});
  };

  const loadHistory = () => {
    setHistLoading(true);
    authAxios
      .get("/admin/sirs/history")
      .then((r) => setHistory(r.data.events || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  };

  const clearHistory = async () => {
    if (!window.confirm("Hapus semua riwayat gangguan SIRS?")) return;
    try {
      await authAxios.delete("/admin/sirs/history");
      toast.success("Riwayat dihapus");
      loadHistory();
    } catch {
      toast.error("Gagal menghapus riwayat");
    }
  };

  const fmtDuration = (sec) => {
    if (sec == null) return null;
    if (sec < 60) return `${sec} detik`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m} menit`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm ? `${h} jam ${rm} menit` : `${h} jam`;
  };

  useEffect(() => {
    if (isSuperadmin) {
      load();
      loadTg();
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  if (!isSuperadmin) {
    return (
      <div
        data-testid="sirs-settings-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">
          Pengaturan Mode SIRS API hanya dapat diakses oleh <b>superadmin</b>.
        </div>
      </div>
    );
  }

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      await authAxios.put("/admin/sirs/mode", { enabled });
      toast.success(enabled ? "Mode SIRS API diaktifkan" : "Mode SIRS API dinonaktifkan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = async (alert_email) => {
    setSavingAlert(true);
    try {
      await authAxios.put("/admin/sirs/mode", { alert_email });
      toast.success(alert_email ? "Email peringatan diaktifkan" : "Email peringatan dinonaktifkan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally {
      setSavingAlert(false);
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await authAxios.post("/admin/sirs/test-alert");
      toast.success(`Email uji terkirim ke ${r.data.sent} superadmin`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim email uji");
    } finally {
      setSendingTest(false);
    }
  };

  const addTgRecipient = async () => {
    const u = tgUsername.trim().replace(/^@/, "");
    if (!u) return;
    setTgBusy(true);
    try {
      await authAxios.post("/admin/telegram/recipients", { username: u });
      toast.success(`@${u} ditambahkan. Minta mereka ketik /siperkasa ke bot lalu Sinkronkan.`);
      setTgUsername("");
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah penerima");
    } finally {
      setTgBusy(false);
    }
  };

  const removeTgRecipient = async (username) => {
    try {
      await authAxios.delete(`/admin/telegram/recipients/${username}`);
      toast.success(`@${username} dihapus`);
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const pollTg = async () => {
    setTgPolling(true);
    try {
      const r = await authAxios.post("/admin/telegram/poll");
      const n = (r.data.newly_registered || []).length;
      toast.success(
        n > 0
          ? `${n} penerima baru terdaftar: ${r.data.newly_registered.map((x) => "@" + x).join(", ")}`
          : "Tidak ada pendaftaran baru. Pastikan mereka sudah mengetik /siperkasa ke bot."
      );
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sinkronisasi");
    } finally {
      setTgPolling(false);
    }
  };

  const toggleTgAlert = async (v) => {
    setTgAlertSaving(true);
    try {
      await authAxios.put("/admin/telegram/alert", { alert_enabled: v });
      toast.success(v ? "Alert Telegram diaktifkan" : "Alert Telegram dinonaktifkan");
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setTgAlertSaving(false);
    }
  };

  const toggleBotEnabled = async (v) => {
    if (!v && !window.confirm(
      "Nonaktifkan Bot Telegram sepenuhnya?\n\nSemua polling & pengiriman pesan ke Telegram akan DIHENTIKAN. Ini melindungi domain rsbhayangkaratarakan.id dari risiko diblokir karena aktivitas bot."
    )) return;
    setTgBotSaving(true);
    try {
      await authAxios.put("/admin/telegram/alert", { bot_enabled: v });
      toast.success(v ? "Bot Telegram diaktifkan" : "Bot Telegram dinonaktifkan sepenuhnya");
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setTgBotSaving(false);
    }
  };

  const toggleTgAutoPoll = async (v) => {
    try {
      await authAxios.put("/admin/telegram/alert", { auto_poll: v });
      toast.success(v ? "Sinkron otomatis diaktifkan" : "Sinkron otomatis dimatikan");
      loadTg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const sendTgTest = async () => {
    setTgTesting(true);
    try {
      const r = await authAxios.post("/admin/telegram/test");
      toast.success(`Pesan uji terkirim ke ${r.data.sent}/${r.data.total} penerima`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pesan uji");
    } finally {
      setTgTesting(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await authAxios.get("/admin/sirs/mode");
      setState(r.data);
      if (r.data.connected) toast.success("Koneksi ke SIRS API berhasil");
      else toast.error("Koneksi ke SIRS API gagal");
    } catch {
      toast.error("Gagal menguji koneksi");
    } finally {
      setTesting(false);
    }
  };

  const enabled = !!state?.enabled;
  const connected = !!state?.connected;

  return (
    <div data-testid="admin-panel-sirs-settings">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Mode SIRS API</h2>
        <p className="text-sm text-slate-500">
          Aktifkan bila SIPERKASA harus mengambil data langsung dari Sistem Informasi
          Rumah Sakit (SIRS). Saat aktif namun koneksi terputus, pengguna akan melihat
          banner gangguan yang informatif.
        </p>
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="space-y-5 max-w-2xl">
          {/* Toggle card */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center">
                  <Server className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900">
                    Mode SIRS API {enabled ? "Aktif" : "Nonaktif"}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {enabled
                      ? "SIPERKASA diharapkan tersambung ke SIRS API secara langsung."
                      : "SIPERKASA memakai data internal/simulasi. Tidak ada banner gangguan."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                disabled={saving}
                onClick={() => toggle(!enabled)}
                data-testid="sirs-mode-toggle"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  enabled ? "bg-blue-700" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Connection status card */}
          <div
            data-testid="sirs-connection-status"
            className={`rounded-3xl border p-6 ${
              !enabled
                ? "bg-slate-50 border-slate-200"
                : connected
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div
                  className={`h-11 w-11 rounded-2xl flex items-center justify-center ${
                    !enabled
                      ? "bg-slate-200 text-slate-500"
                      : connected
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {!enabled ? (
                    <WifiOff className="h-5 w-5" />
                  ) : connected ? (
                    <Wifi className="h-5 w-5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    {!enabled
                      ? "Pemeriksaan koneksi nonaktif"
                      : connected
                      ? "Tersambung ke SIRS API"
                      : "Koneksi SIRS API terputus"}
                    {enabled && connected && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="text-slate-500">
                      Endpoint:{" "}
                      <code className="font-mono text-slate-700 break-all">
                        {state?.base_url || "—"}
                      </code>
                    </div>
                    {enabled && !connected && state?.reason && (
                      <div className="text-amber-800">
                        Alasan:{" "}
                        <code className="font-mono break-all">{state.reason}</code>
                      </div>
                    )}
                    {state?.checked_at && (
                      <div className="text-slate-400">
                        Diperiksa: {new Date(state.checked_at).toLocaleString("id-ID")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={testConnection}
                disabled={testing}
                data-testid="sirs-test-connection"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 transition disabled:opacity-60"
              >
                {testing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Tes Koneksi
              </button>
            </div>
            {enabled && !connected && (
              <div className="mt-4 pt-4 border-t border-amber-200 text-xs text-amber-800">
                Banner gangguan otomatis tampil di halaman SIPERKASA untuk semua pengguna
                selama koneksi belum pulih.
              </div>
            )}
          </div>

          {/* Email alert card */}
          <div
            data-testid="sirs-email-alert-card"
            className="bg-white rounded-3xl border border-slate-100 p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900">
                    Email Peringatan Gangguan
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Kirim email otomatis ke seluruh superadmin saat koneksi SIRS
                    terputus (dan saat pulih kembali). Maks. 1 email / 30 menit selama
                    gangguan berlangsung.
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!state?.alert_email}
                disabled={savingAlert}
                onClick={() => toggleAlert(!state?.alert_email)}
                data-testid="sirs-alert-toggle"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  state?.alert_email ? "bg-indigo-600" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    state?.alert_email ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {!state?.smtp_configured && (
              <div className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  SMTP belum dikonfigurasi di server (.env). Email peringatan tidak akan
                  terkirim sampai SMTP diatur.
                </span>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Penerima ({(state?.alert_recipients || []).length})
              </div>
              <div className="flex flex-wrap gap-2" data-testid="sirs-alert-recipients">
                {(state?.alert_recipients || []).length === 0 ? (
                  <span className="text-xs text-slate-400">Belum ada akun superadmin.</span>
                ) : (
                  (state.alert_recipients || []).map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs"
                    >
                      <Mail className="h-3 w-3 text-slate-400" />
                      {email}
                    </span>
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={sendTest}
                disabled={sendingTest || !state?.smtp_configured}
                data-testid="sirs-send-test-email"
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition disabled:opacity-50"
              >
                {sendingTest ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Kirim Email Uji
              </button>
            </div>
          </div>

          {/* Telegram alert card */}
          <div
            data-testid="sirs-telegram-card"
            className="bg-white rounded-3xl border border-slate-100 p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    Notifikasi Telegram
                    {tg?.configured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <BadgeCheck className="h-3 w-3" /> Bot aktif
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Kirim alert gangguan/pulih ke bot{" "}
                    <a
                      href={tg?.bot_link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sky-600 font-medium hover:underline inline-flex items-center gap-0.5"
                    >
                      @{tg?.bot_username || "rsbhayangkaratarakan_bot"}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    . Hanya penerima terdaftar yang menerima pesan.
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!tg?.alert_enabled}
                disabled={tgAlertSaving || !tg?.configured || tg?.bot_active === false}
                onClick={() => toggleTgAlert(!tg?.alert_enabled)}
                data-testid="telegram-alert-toggle"
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  tg?.alert_enabled ? "bg-sky-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    tg?.alert_enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {!tg?.configured && (
              <div className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Token bot Telegram belum dikonfigurasi di server (.env).</span>
              </div>
            )}

            {/* Master switch — fully enable/disable the bot (protects domain) */}
            {tg?.configured && (
              <div
                data-testid="telegram-master-switch-card"
                className={`mt-4 rounded-2xl border p-4 ${
                  tg?.bot_active === false
                    ? "bg-rose-50 border-rose-200"
                    : "bg-slate-50 border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                        tg?.bot_active === false
                          ? "bg-rose-100 text-rose-600"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      <Power className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                        {tg?.bot_active === false ? "Bot Telegram NONAKTIF" : "Bot Telegram Aktif"}
                        {tg?.env_locked && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full">
                            <ShieldAlert className="h-3 w-3" /> Dikunci server
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Saklar utama. Saat <b>nonaktif</b>, aplikasi berhenti total menghubungi
                        Telegram (tanpa polling &amp; tanpa kirim pesan).
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={tg?.bot_active !== false}
                    disabled={tgBotSaving || tg?.env_locked}
                    onClick={() => toggleBotEnabled(tg?.bot_active === false)}
                    data-testid="telegram-master-toggle"
                    title={tg?.env_locked ? "Dikunci oleh variabel server TELEGRAM_BOT_ENABLED" : undefined}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      tg?.bot_active !== false ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        tg?.bot_active !== false ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                {tg?.env_locked ? (
                  <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-600 border-t border-slate-200/70 pt-3">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-500" />
                    <span>
                      Status bot dikunci oleh variabel server{" "}
                      <code className="font-mono bg-slate-200 px-1 rounded">TELEGRAM_BOT_ENABLED={String(tg?.env_locked_value)}</code>{" "}
                      di <code className="font-mono">backend/.env</code> (mis. untuk <b>production</b>).
                      Saklar UI dinonaktifkan; ubah nilai di server untuk mengaktifkan/menonaktifkan.
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 border-t border-slate-200/70 pt-3">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                    <span>
                      Nonaktifkan bila khawatir domain <b>rsbhayangkaratarakan.id</b> ditandai
                      karena aktivitas bot (mis. blacklist "operating a botnet"). Pengaturan &amp;
                      daftar penerima tetap tersimpan dan bisa diaktifkan lagi kapan saja.
                    </span>
                  </div>
                )}
              </div>
            )}

            {tg?.configured && tg?.bot_active === false && (
              <div
                data-testid="telegram-disabled-banner"
                className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Bot dinonaktifkan — kontrol di bawah tidak berfungsi hingga bot diaktifkan
                  {tg?.env_locked ? " melalui variabel server TELEGRAM_BOT_ENABLED." : " kembali melalui saklar utama di atas."}
                </span>
              </div>
            )}


            {/* Registration steps */}
            <div className="mt-4 p-3 rounded-xl bg-sky-50/60 border border-sky-100 text-xs text-slate-600">
              <b className="text-slate-700">Cara mendaftarkan penerima:</b>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Tambahkan username Telegram di bawah.</li>
                <li>Minta orang tsb membuka bot lalu mengetik <b>/siperkasa</b>.</li>
                <li>Klik <b>Sinkronkan Pendaftaran</b> untuk menangkap akun mereka.</li>
              </ol>
              <div className="mt-2 pt-2 border-t border-sky-100 text-[11px] text-slate-500">
                💬 Penerima terdaftar dapat mengetik <b>/status</b> ke bot kapan saja untuk
                mengecek kondisi koneksi SIRS terkini (Online / Gangguan).
              </div>
            </div>

            {/* Add recipient */}
            <div className="mt-4 flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
                <input
                  value={tgUsername}
                  onChange={(e) => setTgUsername(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTgRecipient()}
                  placeholder="username_telegram"
                  data-testid="telegram-username-input"
                  className="w-full pl-7 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none"
                />
              </div>
              <button
                type="button"
                onClick={addTgRecipient}
                disabled={tgBusy || !tgUsername.trim() || tg?.bot_active === false}
                data-testid="telegram-add-recipient"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50"
              >
                {tgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Tambah
              </button>
            </div>

            {/* Recipients list */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Penerima ({(tg?.recipients || []).length})
              </div>
              <div className="space-y-2" data-testid="telegram-recipients">
                {(tg?.recipients || []).length === 0 ? (
                  <span className="text-xs text-slate-400">Belum ada penerima.</span>
                ) : (
                  tg.recipients.map((r) => (
                    <div
                      key={r.username}
                      className="flex items-center justify-between gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <MessageCircle className="h-4 w-4 text-sky-500 shrink-0" />
                        <span className="text-sm text-slate-700 truncate">@{r.username}</span>
                        {r.registered ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            <BadgeCheck className="h-3 w-3" /> Terdaftar
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            <Clock className="h-3 w-3" /> Menunggu /siperkasa
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTgRecipient(r.username)}
                        data-testid={`telegram-remove-${r.username}`}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                        title="Hapus"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={pollTg}
                  disabled={tgPolling || !tg?.configured || tg?.bot_active === false}
                  data-testid="telegram-sync"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-sky-600 hover:bg-sky-700 text-white transition disabled:opacity-50"
                >
                  {tgPolling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Sinkronkan Pendaftaran
                </button>
                <button
                  type="button"
                  onClick={sendTgTest}
                  disabled={tgTesting || !tg?.configured || tg?.bot_active === false}
                  data-testid="telegram-send-test"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 transition disabled:opacity-50"
                >
                  {tgTesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Kirim Pesan Uji
                </button>
              </div>

              {/* Auto-sync toggle */}
              <label
                data-testid="telegram-autopoll-row"
                className="mt-3 flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer"
              >
                <span className="text-xs text-slate-600">
                  <span className="font-medium text-slate-700">Sinkron otomatis</span>
                  <span className="block text-[11px] text-slate-400">
                    Tangkap pendaftaran baru &amp; jawab perintah /status otomatis tiap{" "}
                    {tg?.auto_poll_interval_sec || 8} detik — tanpa perlu klik manual.
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!tg?.auto_poll}
                  disabled={!tg?.configured || tg?.bot_active === false}
                  onClick={() => toggleTgAutoPoll(!tg?.auto_poll)}
                  data-testid="telegram-autopoll-toggle"
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                    tg?.auto_poll ? "bg-sky-500" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      tg?.auto_poll ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>
            </div>
          </div>

          {/* History timeline card */}
          <div
            data-testid="sirs-history-card"
            className="bg-white rounded-3xl border border-slate-100 p-6"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center">
                  <History className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900">
                    Riwayat Gangguan SIRS
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Catatan waktu tiap koneksi terputus &amp; pulih (maks. 100 terakhir).
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={loadHistory}
                  disabled={histLoading}
                  data-testid="sirs-history-refresh"
                  title="Muat ulang"
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                >
                  {histLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                </button>
                {(history || []).length > 0 && (
                  <button
                    type="button"
                    onClick={clearHistory}
                    data-testid="sirs-history-clear"
                    title="Hapus riwayat"
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {(history || []).length === 0 ? (
              <div className="p-6 rounded-xl bg-slate-50 border border-dashed border-slate-200 text-center text-sm text-slate-400">
                Belum ada catatan gangguan. Timeline akan terisi saat Mode SIRS API aktif
                dan koneksi berubah.
              </div>
            ) : (
              <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4" data-testid="sirs-history-list">
                {history.map((ev, idx) => {
                  const down = ev.type === "down";
                  const dur = fmtDuration(ev.duration_sec);
                  return (
                    <li key={idx} className="ml-4">
                      <span
                        className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white ${
                          down ? "bg-rose-500" : "bg-emerald-500"
                        }`}
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-sm font-semibold ${
                            down ? "text-rose-700" : "text-emerald-700"
                          }`}
                        >
                          {down ? "Koneksi Terputus" : "Koneksi Pulih"}
                        </span>
                        {!down && dur && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                            <Clock className="h-3 w-3" /> berlangsung {dur}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {new Date(ev.at).toLocaleString("id-ID")}
                      </div>
                      {down && ev.reason && (
                        <div className="text-[11px] text-slate-500 mt-1 font-mono break-all">
                          {ev.base_url ? `${ev.base_url} — ` : ""}
                          {ev.reason}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>


        </div>
      )}
    </div>
  );
};

export default SirsSettingsPanel;
